import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  TablePagination,
  Tooltip,
  Typography
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import { WhatsApp } from '@mui/icons-material';
import { usePermissions } from '../../../utils/usePermissions';
import { notify } from '../../notifications';
import { ConfirmDialog, EditEmptyState } from '../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';
import ChangesTable from './changes/ChangesTable';
import { buildWhatsAppMessage } from './changes/changesCalc';
import { copyText } from './changes/copyText';
import { useMathChanges } from './changes/useMathChanges';
import { PRIMARY_BUTTON_SX } from './mathTableStyles';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, getMonthsList } from './report/mathReportCalc';

const BORDER = '1px solid #e2e8f0';

const EditCheck: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canMarkChecked = hasPerm('reports.math.changes.mark_checked');
  const canMarkAll = hasPerm('reports.math.changes.mark_all');
  const canCopyWhatsApp = hasPerm('reports.math.changes.copy_whatsapp');

  const { month, setMonth, rows, loading, error, retry, markingIds, markingAll, markChecked, markAll } = useMathChanges();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  // Число записей в диалоге фиксируется при открытии: после отметки список обновляется под диалогом
  const [confirm, setConfirm] = useState({ open: false, count: 0 });

  const months = useMemo(getMonthsList, []);
  const total = rows ? rows.length : 0;

  // Страница не выходит за последнюю, даже если строк стало меньше (отметили записи, перезагрузка)
  const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  const safePage = Math.min(page, lastPage);
  const pageRows = useMemo(
    () => (rows ? rows.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage) : []),
    [rows, safePage, rowsPerPage]
  );

  const monthLabel = useMemo(() => {
    const found = months.find((item) => item.value === month);
    return found ? found.label : month;
  }, [months, month]);

  const handleMonthChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setMonth(event.target.value);
      setPage(0);
    },
    [setMonth]
  );

  const handlePageChange = useCallback((_event: unknown, next: number) => setPage(next), []);

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!rows || rows.length === 0) return;
    const copied = await copyText(buildWhatsAppMessage(rows));
    if (copied) notify('Текст скопирован в буфер обмена', 'success');
    else notify('Не удалось скопировать текст. Браузер не дал доступ к буферу обмена, скопируйте номера аптек вручную', 'error');
  }, [rows]);

  const handleConfirmAll = useCallback(async () => {
    await markAll();
    setConfirm((prev) => ({ ...prev, open: false }));
  }, [markAll]);

  const handleOpenConfirm = useCallback(() => setConfirm({ open: true, count: total }), [total]);

  const handleCloseConfirm = useCallback(() => setConfirm((prev) => ({ ...prev, open: false })), []);

  const renderContent = () => {
    if (error && !rows) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить изменения"
          text={error}
          action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!rows) {
      return (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <EditEmptyState
          icon={<TaskAltOutlinedIcon />}
          title="Непроверенных изменений нет"
          text="За выбранный месяц новых изменений в мат. отчетах нет."
        />
      );
    }
    return (
      <Paper
        elevation={0}
        sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
        style={{ opacity: loading ? 0.55 : 1 }}
      >
        <ChangesTable
          rows={pageRows}
          firstNumber={safePage * rowsPerPage + 1}
          canMark={canMarkChecked}
          disabled={markingAll}
          markingIds={markingIds}
          onMark={markChecked}
        />
        <TablePagination
          component="div"
          showFirstButton
          showLastButton
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          count={total}
          rowsPerPage={rowsPerPage}
          page={safePage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          sx={{ borderTop: BORDER }}
        />
      </Paper>
    );
  };

  const hasRows = !!rows && rows.length > 0;
  const anyMarking = Object.keys(markingIds).length > 0;

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
      >
        <Select
          size="small"
          value={month}
          onChange={handleMonthChange}
          disabled={markingAll}
          sx={{ minWidth: 200, backgroundColor: '#ffffff', borderRadius: '8px' }}
        >
          {months.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </Select>

        {(canCopyWhatsApp || canMarkAll) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {canCopyWhatsApp && (
              <Tooltip title="Скопировать текст сообщения для WhatsApp" enterDelay={800} arrow>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<WhatsApp />}
                    onClick={handleCopy}
                    disabled={!hasRows || markingAll}
                    sx={ACTION_BUTTON_SX}
                  >
                    Текст для WhatsApp
                  </Button>
                </span>
              </Tooltip>
            )}
            {canMarkAll && (
              <Tooltip title="Снять пометки изменений за выбранный месяц" enterDelay={800} arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<DoneAllIcon />}
                    onClick={handleOpenConfirm}
                    disabled={!hasRows || markingAll || anyMarking}
                    sx={PRIMARY_BUTTON_SX}
                  >
                    Отметить всё
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {renderContent()}

      <ConfirmDialog
        open={confirm.open}
        title="Отметить все изменения проверенными"
        confirmText="Отметить всё"
        loading={markingAll}
        maxWidth="xs"
        onConfirm={handleConfirmAll}
        onClose={handleCloseConfirm}
      >
        <Typography sx={{ color: '#334155', fontSize: '0.92rem' }}>
          У {confirm.count} записей за {monthLabel} будет снята пометка изменения. Действие нельзя отменить.
        </Typography>
      </ConfirmDialog>
    </Box>
  );
};

export default EditCheck;
